<template>
  <div class="page_container content-box">
    <div>
      <woot-button
        color-scheme="success"
        class-names="button--fixed-right-top"
        icon="add-circle"
        @click="$router.push({ name: 'add_outbound_call_campaign' })"
      >
        {{ $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD_OUTBOUND_CALL_CAMPAIGN') }}
      </woot-button>
    </div>
    <OutboundCallReportStatsCard
      :points="staticMetaReports"
      :metrics="metrics"
      :selected-index="currentSelection"
      @onClick="changeSelection"
    />
    <section class="campaign_filters">
      <div class="campaign_filter">
        <SearchInput
          v-model="searchValue"
          :placeholder="$t('CAMPAIGN.FILTERS.SEARCH_PLACEHOLDER')"
          @search="onSearch"
        />
      </div>
      <div class="campaign_filter filters__button-wrap">
        <div v-if="appliedFilters.length" class="filters__applied-indicator" />
        <woot-button
          class="margin-right-small clear"
          color-scheme="secondary"
          data-testid="create-new-contact"
          icon="filter"
          @click="onToggleFilter"
        >
          {{ $t('CONTACTS_PAGE.FILTER_CONTACTS') }}
        </woot-button>
      </div>
    </section>
    <section class="campaign_filters">
      <div class="campaign_filter">
        <p class="input_label">{{ $t('ORDER.LIST_TABLE.DATE.LABEL') }}</p>
        <date-range-picker
          class="font_normal order_start_date"
          :confirm-text="$t('ORDER.LIST_TABLE.DATE_SELECT_CONFIRM_BTN')"
          :placeholder="$t('ORDER.LIST_TABLE.DATE.PLACEHOLDER')"
          :value="dateRange"
          :disabled-date="disableFilterDate"
          @change="handleDateChange"
        />
      </div>
    </section>
    <CampaignListTable
      :is-loading="uiFlags.isFetching"
      :records="campaignRecords"
      :meta="meta"
      :kind="kind"
      @on-delete-click="openDeletePopup"
      @on-sort-change="onSortChange"
      @on-name-click="onCampaignNameClick"
      @on-edit-click="
        row =>
          $router.push({
            name: 'edit_outbound_call_campaign',
            params: { id: row.id },
          })
      "
    />
    <div v-if="meta.count > 10" class="loadMore_btn">
      <woot-button
        variant="clear"
        size="small"
        color-scheme="primary"
        :is-loading="uiFlags.isFetching"
        :is-disabled="paginationCompute.isLastPage"
        @click="onPageChange(Number(meta.current_page) + 1)"
      >
        {{ paginationCompute.btnText }}
      </woot-button>
    </div>

    <woot-modal
      :show.sync="showFiltersModal"
      :on-close="onToggleFilter"
      size="medium"
    >
      <OutboundCampaignAdvancedFilter
        :on-close="onToggleFilter"
        :filter-items="filterItems"
        :initial-applied-filters="appliedFilters"
        :filter-attributes="filterAttributes"
        @applyFilter="onApplyFilter"
        @clearFilters="clearFilters"
      />
    </woot-modal>

    <woot-delete-modal
      :show.sync="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('CAMPAIGN.DELETE.CONFIRM.TITLE')"
      :message="$t('CAMPAIGN.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="$t('CAMPAIGN.DELETE.CONFIRM.YES')"
      :reject-text="$t('CAMPAIGN.DELETE.CONFIRM.NO')"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import helperMixin from 'shared/mixins/helperMixin';
import alertMixin from 'shared/mixins/alertMixin';
import DateRangePicker from 'components/ui/DateRangePicker.vue';
import moment from 'moment';
import CampaignListTable from './CampaignListTable.vue';
import SearchInput from 'dashboard/components/ui/SearchInput.vue';
import {
  AI_CAMPAIGN_STATUS,
  AI_CAMPAIGN_SORT_LIST,
  OutboundCallCampaignAttributeList,
  OutboundCallCampaignFilterTypes,
} from '../broadcastAgent/aiCampaignConstant';
import { OUTBOUND_CALL_CAMPAIGN_STATUS_METRIC } from '../broadcastAgent/aiCampaignConstant';
import OutboundCallReportStatsCard from './OutboundCallReportStatsCard.vue';
import OutboundCampaignAdvancedFilter from './OutboundCampaignAdvancedFilter.vue';

export default {
  components: {
    DateRangePicker,
    OutboundCallReportStatsCard,
    CampaignListTable,
    SearchInput,
    OutboundCampaignAdvancedFilter,
  },
  mixins: [helperMixin, alertMixin],
  props: {
    kind: {
      type: String,
      default: '',
    },
  },
  data() {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 1);

    return {
      dateRange: [startDate, endDate],
      currentSelection: 999,
      searchValue: '',
      selectedStatus: null,
      sortBy: null,
      showDeleteConfirmationPopup: false,
      selectedCampaign: {},
      sortConfig: { name: 'asc' },
      showFiltersModal: false,
      appliedFilters: [],
      filterItems: OutboundCallCampaignFilterTypes.map(filter => ({
        ...filter,
        attributeName: this.$t(`${filter.attributeI18nKey}`),
      })),
      staticMetaReports: {
        pending: 0,
        active: 0,
        running: 0,
        completed: 0,
        deactivate: 0,
      },
      debouncedSearch: null,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      uiFlags: 'campaign_agents/getUIFlags',
      meta: 'campaign_agents/getMeta',
      campaignRecords: 'campaign_agents/getCampaignAgents',
    }),
    metaReports() {
      const defaultReports = {
        pending: 0,
        active: 0,
        running: 0,
        completed: 0,
        deactivate: 0,
      };
      return { ...defaultReports, ...(this.meta.reports || {}) };
    },
    paginationCompute() {
      const totalPages = Math.ceil(this.meta.count / (this.meta.pageSize || 1));
      const currentPage = Number(this.meta.current_page);
      const isLastPage = currentPage >= totalPages;
      const currentCount =
        Math.min(currentPage * this.meta.pageSize, this.meta.count) || 0;

      const btnText = `${this.$t(
        'CAMPAIGN.LLM_CHATBOT.REPORT.LOAD_MORE_BTN'
      )} (${currentCount}/${this.meta.count})`;

      return {
        isLastPage,
        btnText,
      };
    },
    metrics() {
      return OUTBOUND_CALL_CAMPAIGN_STATUS_METRIC.map(el => ({
        ...el,
        NAME: this.$t(el.NAME),
      }));
    },
    statusList() {
      return AI_CAMPAIGN_STATUS.map(el => ({
        ...el,
        title: this.$t(el.title),
      }));
    },
    sortByList() {
      return AI_CAMPAIGN_SORT_LIST.map(el => ({
        ...el,
        title: this.$t(el.title),
      }));
    },
    filterAttributes() {
      return OutboundCallCampaignAttributeList.map(el => ({
        ...el,
        name: this.$t(el.name),
      }));
    },
  },
  watch: {
    metaReports: {
      handler(newVal) {
        if (this.meta.reports) {
          this.staticMetaReports = { ...newVal };
        }
      },
      deep: true,
    },
  },
  mounted() {
    // Initial setup, don't fetch here - let activated handle it
  },
  activated() {
    this.clearFiltersOnPageLoad();
  },
  methods: {
    clearFiltersOnPageLoad() {
      // Clear all filters and reset to initial state
      this.searchValue = '';
      this.appliedFilters = [];
      this.selectedStatus = null;
      this.sortConfig = { name: 'asc' };
      this.dateRange = [
        new Date(new Date().setMonth(new Date().getMonth() - 1)),
        new Date(),
      ];
      this.currentSelection = 999;
      // Fetch fresh data with cleared filters
      this.fetchCampaignAgents({ page: 1 });
    },
    async fetchCampaignAgents(newQuery = {}) {
      try {
        const query = {
          kind: this.kind,
          page: this.meta.current_page,
          pageSize: this.meta.pageSize,
          start_date: this.formatStartEndDate(
            this.dateRange[0],
            this.dateRange[1]
          ).start_date,
          end_date: this.formatStartEndDate(
            this.dateRange[0],
            this.dateRange[1]
          ).end_date,
          q: this.searchValue,
          status: this.selectedStatus?.id,
          sort: this.getSortAttribute(this.sortConfig),
          ...newQuery,
        };

        // If there are applied filters, use filter API
        if (this.appliedFilters?.length) {
          const payload = this.appliedFilters
            .map(el => {
              const temp = { ...el };
              if (
                el.attribute_key === 'status' ||
                (typeof el.values === 'object' && el.values?.id)
              ) {
                temp.values = [el.values.id];
              } else if (Array.isArray(el.values)) {
                temp.values = el.values ? [...el.values] : [];
              } else {
                temp.values = el.values ? [el.values] : '';
              }

              if (
                el.filter_operator === 'is_present' ||
                el.filter_operator === 'is_not_present'
              ) {
                delete temp.values;
              }

              return temp;
            })
            .filter(
              el =>
                el.filter_operator === 'is_present' ||
                el.filter_operator === 'is_not_present' ||
                el.values
            );

          // remove the query_operator on the last filter item
          if (payload.length) {
            delete payload[payload.length - 1].query_operator;
          }

          const { status, ...queryWithoutStatus } = query;
          this.$store.dispatch(
            'campaign_agents/fetchCampaignAgentFilterList',
            this.cleanObject({ ...queryWithoutStatus, payload })
          );
        } else {
          this.$store.dispatch(
            'campaign_agents/fetchCampaignAgent',
            this.cleanObject(query)
          );
        }
      } catch (error) {
        console.error('Error fetching campaign agents:', error);
      }
    },
    onPageChange(page) {
      this.fetchCampaignAgents({ page });
    },
    async handleDateChange(dateRange) {
      this.dateRange = dateRange;
      const date = this.formatStartEndDate(dateRange[0], dateRange[1]);
      this.fetchCampaignAgents({
        page: 1,
        start_date: date.start_date,
        end_date: date.end_date,
      });
    },
    disableFilterDate(date) {
      const today = moment();
      const monthAgo = 6;
      const nMonthsAgo = moment().subtract(monthAgo, 'months');
      const targetDate = moment(date);
      return (
        targetDate.isAfter(today, 'day') ||
        targetDate.isBefore(nMonthsAgo, 'day')
      );
    },
    onSearch(value) {
      this.searchValue = value;
      // Clear existing timeout if any
      if (this.debouncedSearch) {
        clearTimeout(this.debouncedSearch);
      }
      // Set new timeout
      this.debouncedSearch = setTimeout(() => {
        this.fetchCampaignAgents({ page: 1 });
      }, 300);
    },
    applyFilterByStatuses(selectedOption) {
      if (this.selectedStatus?.id === selectedOption?.id) {
        this.selectedStatus = null;
      } else {
        this.selectedStatus = selectedOption;
      }
      this.fetchCampaignAgents({ page: 1 });
    },
    applyFilterBySortBy(selectedOption) {
      this.sortBy = selectedOption;
      this.fetchCampaignAgents({
        order: selectedOption.order,
        direction: selectedOption.direction,
        page: 1,
      });
    },
    onToggleFilter() {
      this.showFiltersModal = !this.showFiltersModal;
    },
    onApplyFilter(filters) {
      this.appliedFilters = filters;
      this.fetchCampaignAgents({ page: 1 });
      this.onToggleFilter();
    },
    clearFilters() {
      this.appliedFilters = [];
      this.fetchCampaignAgents({ page: 1 });
    },
    changeSelection() {
      // Filter by status click handler - functionality removed as it's handled by filter modal
    },
    openDeletePopup(response) {
      this.showDeleteConfirmationPopup = true;
      this.selectedCampaign = response;
    },
    closeDeletePopup() {
      this.showDeleteConfirmationPopup = false;
    },
    async confirmDeletion() {
      this.closeDeletePopup();
      try {
        await this.$store.dispatch(
          'campaign_agents/deleteCampaignAgent',
          this.selectedCampaign?.id
        );
        this.showAlert(this.$t('CAMPAIGN.DELETE.API.SUCCESS_MESSAGE'));
        this.selectedCampaign = {};
      } catch (error) {
        this.showAlert(this.$t('CAMPAIGN.DELETE.API.ERROR_MESSAGE'));
      }
    },
    onSortChange(params) {
      let sortDetails = {};
      Object.keys(params).forEach(key => {
        if (params[key] !== '') {
          sortDetails = {};
          sortDetails.key = key;
          sortDetails.order = params[key];
        }
      });
      this.sortConfig = params;
      const sort = this.getSortAttribute(sortDetails);
      this.fetchCampaignAgents({ sort, page: 1 });
    },
    getSortAttribute(newSort = {}) {
      let sortAttr = Object.keys({ ...this.sortConfig, ...newSort }).reduce(
        (acc, sortKey) => {
          const sortOrder = this.sortConfig[sortKey];
          if (sortOrder) {
            const sortOrderSign = sortOrder === 'asc' ? '' : '-';
            return `${sortOrderSign}${sortKey}`;
          }
          return acc;
        },
        ''
      );
      if (!sortAttr) {
        this.sortConfig = { name: 'asc' };
        sortAttr = 'name';
      }
      return sortAttr;
    },
    onCampaignNameClick(row) {
      this.$router.push({
        name: 'outbound_call_campaign_details',
        params: { campaignId: row.id },
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import '~dashboard/assets/scss/widgets/_ps_table.scss';

.campaign_filters {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;

  .campaign_filter {
    p {
      margin-bottom: 8px;
    }
  }
}
.loadMore_btn button {
  display: block;
  margin: auto;
}

.filters__button-wrap {
  position: relative;

  .filters__applied-indicator {
    position: absolute;
    height: var(--space-small);
    width: var(--space-small);
    top: var(--space-smaller);
    right: var(--space-slab);
    background-color: var(--s-500);
    border-radius: var(--border-radius-rounded);
  }
}
</style>
